import { FileButton } from '@mantine/core';
import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { IconAlertCircle, IconUpload } from '@tabler/icons-react';
import axios from 'axios';
import React from 'react';
import { resolveApiOrigin } from 'src/api/apiOrigin';
import { UserRateImportResult } from 'src/api/FiretrolApiClient';
import { getAuthOptions } from 'src/api/helpers';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { ImportResults } from './ImportResults';

interface ImportUserRatesModalProps {
  opened: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

const uploadUserRatesCsv = async (
  file: File,
): Promise<UserRateImportResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const baseUrl = resolveApiOrigin(import.meta.env.VITE_API);
  const authOptions = await getAuthOptions();

  if (!authOptions) {
    throw new Error('Authentication required');
  }

  const response = await axios.post(`${baseUrl}/UserRate/import`, formData, {
    headers: {
      Authorization: `Bearer ${authOptions.headers.Authorization}`,
      'Content-Type': 'multipart/form-data',
    },
  });

  if (response.status === 200) {
    const result =
      typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data;
    return result as UserRateImportResult;
  }

  throw new Error('Failed to import user rates');
};

export const ImportUserRatesModal: React.FC<ImportUserRatesModalProps> = ({
  opened,
  onClose,
  onImportComplete,
}) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [importResult, setImportResult] =
    React.useState<UserRateImportResult | null>(null);

  const handleFileSelect = (selectedFile: File | null) => {
    if (!selectedFile) {
      setFile(null);
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== 'csv') {
      setError('Please select a CSV file');
      setFile(null);
      return;
    }

    setError(null);
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError(null);
    setImportResult(null);

    try {
      const result = await uploadUserRatesCsv(file);
      setImportResult(result);
    } catch (err: any) {
      let errorMessage = 'Failed to import user rates';

      if (err.response) {
        if (err.response.status === 400) {
          errorMessage =
            err.response.data?.message || 'Invalid file format or missing file';
        } else if (err.response.status === 401 || err.response.status === 403) {
          errorMessage = 'You do not have permission to import user rates';
        } else if (err.response.status === 500) {
          errorMessage = 'Server error occurred while importing user rates';
        } else {
          errorMessage = err.response.data?.message || errorMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setImportResult(null);
    setUploading(false);
    onClose();
  };

  const handleImportComplete = () => {
    handleClose();
    onImportComplete();
  };

  const canUpload = file && !uploading && !importResult;

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title="Import User Rates"
      size="xl"
      centered
    >
      <Stack gap="md" pos="relative">
        <LoadingOverlay visible={uploading} />

        {!importResult ? (
          <>
            <Text size="sm" c="dimmed">
              Select a CSV file to import user rate data. The file will be
              validated and processed.
            </Text>

            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Error"
                color="red"
                variant="light"
              >
                {error}
              </Alert>
            )}

            <Group>
              <FileButton
                onChange={handleFileSelect}
                accept=".csv"
                disabled={uploading}
              >
                {(props) => (
                  <BasicButton
                    {...props}
                    leftSection={<IconUpload size={20} />}
                    variant="light"
                    disabled={uploading}
                  >
                    Select CSV File
                  </BasicButton>
                )}
              </FileButton>

              {file && (
                <Text size="sm" c="dimmed">
                  Selected: {file.name}
                </Text>
              )}
            </Group>

            <Group justify="flex-end">
              <SecondaryButton onClick={handleClose} disabled={uploading}>
                Cancel
              </SecondaryButton>
              <BasicButton
                onClick={handleUpload}
                disabled={!canUpload}
                loading={uploading}
              >
                Upload and Import
              </BasicButton>
            </Group>
          </>
        ) : (
          <ImportResults
            result={importResult}
            onClose={handleImportComplete}
            onRetry={() => {
              setImportResult(null);
              setFile(null);
            }}
          />
        )}
      </Stack>
    </BaseModal>
  );
};
